/**
 * Free pictures for users, the slug will be the id of the picture
 *
 */
import { useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import {
	json,
	unstable_parseMultipartFormData as parseMultipartFormData,
	unstable_createMemoryUploadHandler as createMemoryUploadHandler,
	type DataFunctionArgs,
	type LoaderFunctionArgs,
	type MetaArgs,
} from '@remix-run/node'
import {
	Form,
	Link,
	useActionData,
	useLoaderData,
	useNavigate,
	useNavigation,
} from '@remix-run/react'
import { useEffect, useState } from 'react'
import { ClientOnly } from 'remix-utils/client-only'
import { z } from 'zod'
import { ErrorListAlert } from '#app/components/forms.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { invalidateClientCache } from '#app/utils/client-side-caching/store.ts'
import { prisma } from '#app/utils/db.server.ts'
import { generateImageWithSwapUrl } from '#app/utils/generate-image.server.ts'
import * as gtag from '#app/utils/gtags.client.tsx'
import { TipsBox } from './_app+/me+/__upload-files.tsx'
import {
	ImageChooser,
	ImageFieldsetSchema,
	MAX_UPLOAD_SIZE,
} from './_app+/me+/components/image-chooser.tsx'
import { ResultImage } from './_app+/me+/components/result-image.tsx'

export const handle: SEOHandle = {
	getSitemapEntries: async request => {
		try {
			const pages = await prisma.freePhotos.findMany({ select: { slug: true } })
			return pages.map(page => {
				return { route: `/fun/${page.slug}`, priority: 0.7 }
			})
		} catch (e) {
			console.error(e)
			return []
		}
	},
}

const fakeImageGet = async (file: string) => {
	return new Promise<string | null>(resolve => {
		setTimeout(() => {
			resolve(file + '?random=' + Math.random())
		}, 500)
	})
}

const UploadFilesSchema = z
	.object({
		id: z.string().optional(),
		user_image: ImageFieldsetSchema.optional().nullable(),
		swap_image_url: z.string(), // url from us
		user_image_url: z.string().optional(),
		result_image: z.string().optional(),
		save_user_image: z.boolean().optional(),
	})
	.refine(
		data => Boolean(data.user_image_url) || Boolean(data.user_image?.file),
		'Please upload or select a photo of your face',
	)

export async function loader({ params }: LoaderFunctionArgs) {
	let photo = await prisma.freePhotos.findUnique({
		where: { slug: params.slug },
	})

	if (!photo) {
		throw new Response('Not found', { status: 404 })
	}

	return {
		url: photo.url,
		description: photo.description,
	}
}

function getTitle(description: string | null | undefined) {
	if (description) {
		return `Swap your face with ${description}`
	}
	return 'Swap your face with any picture'
}

export function meta({ data }: MetaArgs<typeof loader>) {
	let title = getTitle(data?.description)
	return [
		{ title },
		{
			name: 'description',
			content:
				"Swap your face with any picture in seconds. Just 2 clicks and you're done!",
		},
		{
			name: 'twitter:card',
			content: 'summary_large_image',
		},
		{
			name: 'twitter:site',
			content: '@swapmyface',
		},
		{
			name: 'twitter:creator',
			content: '@swapmyface',
		},
		{
			name: 'twitter:title',
			content: title,
		},
		{
			name: 'twitter:description',
			content:
				"Swap your face with any picture in seconds. Just 2 clicks and you're done!",
		},
		{
			name: 'twitter:image',
			content: data?.url,
		},
		{
			property: 'og:image',
			content: data?.url,
		},
		{ property: 'og:url', content: 'https://swapmyface.app' },
		{ property: 'og:type', content: 'website' },
		{
			property: 'og:title',
			content: title,
		},
		{
			name: 'robots',
			content: 'max-image-preview:large',
		},
	]
}

export async function action({ request }: DataFunctionArgs) {
	let formData
	try {
		formData = await parseMultipartFormData(
			request,
			createMemoryUploadHandler({ maxPartSize: MAX_UPLOAD_SIZE + 1024 * 1024 }),
		)
	} catch (e) {
		return json({
			status: 'error',
			submission: null,
			result: null,
			message: `There was an error uploading the images, please try again. If the error persists use another image of less size.`,
		})
	}

	const submission = await parse(formData, {
		schema: UploadFilesSchema.transform(async (data, ctx) => {
			if (
				!data.user_image_url &&
				(!data.user_image?.file || data.user_image.file.size <= 0)
			) {
				ctx.addIssue({
					path: ['user_image', 'user_image_url'],
					code: z.ZodIssueCode.custom,
					message: 'Please upload or select a photo of your face',
				})
				return z.NEVER
			}
			return data
		}),
		async: true,
	})

	if (submission.intent !== 'submit') {
		return json({
			status: 'idle',
			submission,
			result: null,
			message: '',
		} as const)
	}

	if (!submission.value) {
		return json(
			{ status: 'error', submission, result: null, message: '' } as const,
			{
				status: 400,
			},
		)
	}

	let userImage = submission.value.user_image?.file
	let swapImageUrl = submission.value.swap_image_url

	let result: string | null
	if (0) {
		// result = await fakeImageGet('https://picsum.photos/seed/picsum/200/300')
		result = await fakeImageGet(
			'http://localhost:3000/replicate-prediction-xd2gnibbrj62a3fwd3s5fa2qgq.jpg',
		)
	} else {
		userImage = userImage as File
		result = await generateImageWithSwapUrl(userImage, swapImageUrl)
	}

	if (!result || typeof result !== 'string') {
		return json(
			{
				status: 'error_image',
				submission,
				result: null,
				message:
					'There was an error generating the image, please try again. If the error persists use another image ' +
					'of less size. Please note that if you are trying to put your face in a photo of an animal or something that is not a human face ' +
					'it can fail to generate the image.',
			} as const,
			{
				status: 400,
			},
		)
	}

	return json({ status: 'success', result, submission, message: '' } as const)
}

export default function UploadFiles() {
	const response = useActionData<typeof action>()
	const resultImage = response?.result
	const errorMessage = response?.message
	const navigate = useNavigate()
	const data = useLoaderData<typeof loader>()

	const [image, setImage] = useState<string>(resultImage ?? '')
	useEffect(() => {
		setImage(resultImage ?? '')
		invalidateClientCache('userSavedImages')
	}, [resultImage])

	const navigation = useNavigation()
	const isPending = navigation.state === 'submitting'

	const [form, fields] = useForm({
		id: 'images-upload',
		constraint: getFieldsetConstraint(UploadFilesSchema),
		lastSubmission: response?.submission || undefined,
		onValidate({ formData }) {
			return parse(formData, { schema: UploadFilesSchema })
		},
		defaultValue: {
			user_image: {},
		},
	})

	function getErrorList() {
		if (errorMessage) {
			return [errorMessage]
		}
		if (form.errors.length > 0) {
			return form.errors
		}

		return []
	}
	let swapUrl = data.url

	return (
		<div className="container flex flex-col gap-8">
			<Spacer size="4xs" />
			<h1 className="text-h2">{getTitle(data.description)}</h1>
			<Spacer size="4xs" />
			<ClientOnly>{() => <TipsBox />}</ClientOnly>
			<Form
				{...form.props}
				method="post"
				className="flex w-full flex-col justify-between gap-4 md:flex-row"
				encType="multipart/form-data"
				onSubmit={() => {
					gtag.event({
						action: 'submit photos anonymously',
						category: 'upload-anon',
						label: 'upload-anon',
					})
				}}
			>
				<div className="flex-[1]">
					<UserImage fields={fields} onChange={() => setImage('')} />
				</div>
				<div className="flex flex-col items-center justify-center gap-6">
					<Icon
						className="aspect-square h-auto w-[35%] rotate-180 md:mt-0 md:rotate-90"
						name="merge"
					/>
					<Button disabled={isPending} type="submit" onClick={scrollToResult}>
						Merge my face <span className="text-xs opacity-60">&nbsp;~5s</span>
					</Button>
					{!!resultImage && (
						<Button type="button" onClick={() => navigate(0)}>
							Do another one
						</Button>
					)}
				</div>
				<div className="mt-12 flex-[1] md:mt-0">
					<Label>We'll put your face into this photo!</Label>
					{swapUrl && (
						<>
							<img
								className="h-auto w-full rounded-md"
								src={swapUrl}
								alt="target of face swap"
							/>
							<input name="swap_image_url" type="hidden" value={swapUrl} />
						</>
					)}
					<Spacer size="4xs" />
					<div className="flex w-full flex-row items-center justify-between gap-2 rounded-md border-[1px] px-4 py-2">
						<span>Signup to select any photo you want</span>
						<Link
							className="bg-app-gradient rounded-md p-2 px-4 text-sm text-white"
							prefetch="viewport"
							to="/signup"
						>
							Sign up
						</Link>
					</div>
				</div>
			</Form>
			<div className="flex w-full">
				<span className="hidden flex-1 md:inline" />
				<div className="flex-1">
					{getErrorList().length > 0 && (
						<div className="mb-2">
							<ErrorListAlert
								elementClassName="text-md"
								id="generation-error"
								errors={getErrorList()}
							>
								<Button
									size="sm"
									type="submit"
									variant="ghost"
									form="images-upload"
								>
									Try again
								</Button>
							</ErrorListAlert>
						</div>
					)}
					<ResultImage dontAllowUpscale src={image ?? ''} loading={isPending} />
					<Spacer size="4xs" />
					<Link
						className="bg-app-gradient block w-full rounded-md px-8 py-3 text-center text-white"
						to="/signup"
					>
						Signup to increase resolution
					</Link>
				</div>
				<span className="hidden flex-1 md:inline" />
			</div>
			<Spacer size="xs" />
		</div>
	)
}

function scrollToResult() {
	setTimeout(() => {
		let resultEl = document.getElementById('result-image')
		if (resultEl) {
			resultEl.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			})
		}
	}, 200)
}

function UserImage(props: any) {
	return (
		<div>
			<Label htmlFor={props.fields.user_image.id}>A photo of your face</Label>
			<div className="h-2" />
			<div className="rounded-md border-[1px] p-4">
				<ImageChooser
					config={props.fields.user_image}
					onChange={() => props.onChange()}
				/>
			</div>
		</div>
	)
}
